import { useCallback, useEffect, useState } from 'react';
import ReactGA from 'react-ga4';

import type { MeUserFragment } from '../../api/core/generated';
import { googleAnalyticsId, isProd } from '../../environment';
import { useGetUser } from '../use-get-user';
import { useEffectOnce } from '../utils';

export const isGoogleAnalyticsEnabled = Boolean(googleAnalyticsId);

export const useGoogleAnalytics = () => {
  const [isInitialized, setIsInitialized] = useState(false);

  const { user } = useGetUser();

  const initialize = useCallback(() => {
    if (!googleAnalyticsId || isInitialized) return;

    ReactGA.initialize(googleAnalyticsId, {
      testMode: !isProd,
      gtagOptions: {
        debug_mode: !isProd,
      },
    });

    setIsInitialized(true);
  }, [isInitialized]);

  const identifyUser = useCallback(
    (user: Nullable<MeUserFragment>) => {
      if (!isInitialized || !user) return;

      ReactGA.set({
        user_id: user.id.toString(),
        custom_map: {
          dimension1: user.email ?? 'No User Email',
          dimension2: !(!user.profile?.first_name && !user.profile?.last_name)
            ? `${user.profile?.first_name ?? ''} ${user.profile?.last_name ?? ''}`.trimStart()
            : 'No User Name',
        },
      });
    },
    [isInitialized],
  );

  useEffectOnce(() => {
    initialize();
  });

  useEffect(() => {
    if (!user) return;
    identifyUser(user);
  }, [user, identifyUser]);

  return {
    isInitialized,
    identifyUser,
  };
};
